import React from 'react'
import { StartupDetail } from 'types/common'
import { useIsLoggedIn, useStartupIsInterestedUpdate } from 'utils/hooks'
import { useStores } from 'stores/globals'
import { Column, Row, SpaceBetween } from 'components/common/layout'
import { BaseText, BoldText } from 'components/common/text'
import { Hashtag } from 'components/common/hashtag'
import styled from 'styled-components'
import { setStartupIsInterested } from 'utils/handlers'
import { StartupLogo } from 'components/startup/startup-logo'
import { analytics } from 'infra/analytics'
import { getDiffFromPast } from 'utils/formatters'
import { Tag, TagSize, TagType } from 'components/common/tag'
import { ChipWrapper } from 'components/common/chip'
import { InterestIcon } from 'components/misc/interest-icon'
import { IoShareSocialOutline } from 'react-icons/io5'
import { HOST_BASE_URL } from 'infra/constants'
import toast from 'react-hot-toast'

// 스타트업 상단 기본정보 컨테이너
export const StartupBaseInfo: React.FC<{
  data: StartupDetail
}> = ({ data }) => {
  const isLoggedIn = useIsLoggedIn()
  const { loginStore } = useStores()
  const openLoginModal = () => loginStore.setIsModalOpen(true)
  useStartupIsInterestedUpdate([data.id], `/startups/${data.id}`)
  const toolbar = (
    <Row style={{ gap: 12, flex: 'none' }}>
      <Row style={{ cursor: 'pointer', flex: 'none' }}>
        <IoShareSocialOutline
          size={24}
          onClick={() => {
            window.navigator.clipboard.writeText(
              `${HOST_BASE_URL}/startups/${data.id}`,
            )
            toast.success('클립보드에 복사되었습니다.')
          }}
        />
      </Row>
      <InterestIcon
        size={24}
        onClick={() => {
          if (!isLoggedIn) return openLoginModal()
          setStartupIsInterested(data.id, data.isInterested)
          analytics.track('compDetail_jsu_click_saveBtn_comp', {
            startupId: data.id,
            startupName: data.name,
          })
        }}
        isInterested={data.isInterested}
      />
    </Row>
  )

  return (
    <>
      <StartupBaseInfoDesktop>
        <Column style={{ flex: 1, gap: 24 }}>
          <SpaceBetween style={{ alignItems: 'start', gap: 40 }}>
            <Row style={{ gap: 16, alignItems: 'start', width: '100%' }}>
              <StartupLogo src={data.thumbnail} size={60} />
              <Column style={{ gap: 8 }}>
                <ChipWrapper>
                  <StartupName>{data.name}</StartupName>
                  {getDiffFromPast(data.createdAt) <= 14 && <NewTag />}
                </ChipWrapper>
                <IntroText>{data.briefIntro}</IntroText>
              </Column>
            </Row>
            {toolbar}
          </SpaceBetween>
          {data.tags.length > 0 && <Hashtag data={data.tags} />}
        </Column>
      </StartupBaseInfoDesktop>
      <StartupBaseInfoMobile>
        <Row
          style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Column style={{ gap: 16 }}>
            <StartupLogo src={data.thumbnail} size={58} />
            <ChipWrapper>
              <StartupName>{data.name}</StartupName>
              {getDiffFromPast(data.createdAt) <= 14 && <NewTag />}
            </ChipWrapper>
          </Column>
          {toolbar}
        </Row>
        <Column style={{ gap: 20 }}>
          <IntroText>{data.briefIntro}</IntroText>
          {data.tags.length > 0 && <Hashtag data={data.tags} />}
        </Column>
      </StartupBaseInfoMobile>
    </>
  )
}

const NewTag = () => (
  <Tag variant={TagType.BLUE} size={TagSize.EXTRA_SMALL}>
    신규 기업
  </Tag>
)

const IntroText = styled(BaseText)`
  line-height: 1.4;
  @media ${(p) => p.theme.deviceSize.desktop} {
    font-size: ${(p) => p.theme.fontSize.desc};
  }
`

const StartupName = styled(BoldText)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h4};
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    font-size: ${(p) => p.theme.fontSize.h3};
  }
`

const StartupBaseInfoDesktop = styled(Row)`
  align-items: flex-start;
  @media ${(p) => p.theme.deviceSize.mobile} {
    display: none;
  }
`

const StartupBaseInfoMobile = styled(Column)`
  gap: 8px;
  @media ${(p) => p.theme.deviceSize.desktop} {
    display: none;
  }
`
